import React, { createContext, useContext, useState, useEffect } from "react";
import docCookies from "mozilla-doc-cookies";
import { Helmet } from "react-helmet";
import { SiteContext } from "./Site";
import {
	fetchPageData,
	transformSingleEntity,
	findConfigFromType,
	getQueryNode,
} from "./../";

export const SingleContext = createContext();
export const ActionsContext = createContext();

/**
 * A page wrapper to process page specific data and show login UI if needed
 *
 * @param props.LoginForm a login form component coming from the design system plugin
 * @param props.PublicLayout a public layout wrapper for the site: usually includes public header and footer
 * @param props.PrivateLayout a private layout wrapper for the site: usually includes private header and footer
 */

function Page({ LoginForm, PublicLayout, PrivateLayout, children }) {
	const {
		pageData,
		isAuthenticated,
		dispatch,
		location,
		bundles,
		layouts,
		typeDefinitions,
		isPrivate,
		configs,
		siteKey,
		type,
		themeConfig,
		title,
		twitterUsername,
		sitemap,
		isIframe,
		setPreviewing,
		cookieNoticeExists,
		cookieAgreementOnlyinHome,
	} = useContext(SiteContext);

	const _query_node = getQueryNode(location.search);
	const full_entity = pageData.relationships
		? transformSingleEntity({
				entity: pageData,
				bundles,
				layouts,
				typeDefinitions,
				themeConfig,
				configs,
				sitemap,
		  })
		: _query_node
		? {}
		: { ...pageData };
	const [entity, setEntity] = useState(full_entity);
	const [fetching, setFetching] = useState(false);
	const [queryNode, setQueryNode] = useState(_query_node);
	const [htmlClass, setHtmlClass] = useState(getHtmlClass());
	const [showCookieNotice, setShowCookieNotice] = useState(null);

	/**
	 * fetch page main entity and relationships if private
	 */
	useEffect(() => {
		(async function () {
			if (
				((pageData.private && isAuthenticated && !entity.attributes) || isIframe) &&
				!fetching
			) {
				const _dispatch = isAuthenticated ? dispatch : null;
				setFetching(true);
				let _endpoint = queryNode
					? `node/${queryNode[0]}/${queryNode[1]}`
					: pageData.endpoint;

				if (!_endpoint) {
					console.error("no endpoint found for page");
					return;
				}

				let fetchedData = await fetchPageData({
					endpoint: _endpoint,
					bundles,
					layouts,
					dispatch: _dispatch,
					ancestors: pageData.ancestors,
					configs,
				});
				const _type = entity.type || fetchedData.type;
				if (fetchedData) {
					const config = findConfigFromType(_type, configs);
					let _e = {
						config,
						...pageData,
						...fetchedData,
					};
					let _full_e = transformSingleEntity({
						entity: _e,
						bundles,
						layouts,
						typeDefinitions,
						themeConfig,
						configs,
					});
					setEntity(_full_e);
				}
				setFetching(false);
				setPreviewing(false);
			}
		})();
	}, [isAuthenticated, queryNode, isIframe]);

	/**
	 * if location changes, look for node param in URL
	 * and trigger a dynamic fetch for that node
	 */
	useEffect(() => {
		let _query_node = getQueryNode(location.search);
		if (_query_node || (!_query_node && queryNode)) {
			setEntity({});
			setQueryNode(_query_node);
		}
	}, [location]);

	/**
	 * add custom page classes to body
	 */
	useEffect(() => {
		let clss = getHtmlClass(isPrivate, entity);
		setHtmlClass(clss);
	}, [isPrivate, entity]);

	function getHtmlClass(isPrivate, entity) {
		let clss = `site-${siteKey} ${
			entity && entity.attributes && entity.attributes.field_css_class
				? entity.attributes.field_css_class
				: ""
		} ${isPrivate ? "is-private" : "is-public"} type-${type}`;
		if (entity) {
			clss += entity.isHome ? " is-home" : " not-home";
			if (!entity.isHome && !entity.disableTitleBar) {
				clss += " has-title-bar";
			} else {
				clss += " no-title-bar";
			}
			if (entity.hasClosingSection) {
				clss += " has-closing-section";
			} else {
				clss += " no-closing-section";
			}
			if (entity.bannerLayout) {
				clss += ` banner-layout-${entity.bannerLayout}`;
			}
		}
		return clss;
	}

	/**
	 * detect cookie agreement status
	 */
	useEffect(() => {
		function showOnPage() {
			const _isHome = entity && entity.isHome;
			return _isHome || (!_isHome && !cookieAgreementOnlyinHome);
		}
		function alreadyAgreed() {
			let _agreed =
				typeof window !== "undefined" ? docCookies.getItem("cookie_agreed") : true;
			if (!_agreed) {
				return false;
			}
			_agreed = _agreed === "true";
			return _agreed;
		}
		if (entity && cookieNoticeExists) {
			if (showCookieNotice === null) {
				// first time
				let _show = showOnPage() && !alreadyAgreed();
				setShowCookieNotice(_show);
			} else if (showCookieNotice === false) {
				// if agreed, store the cookie
				if (typeof window !== "undefined" && !alreadyAgreed()) {
					docCookies.setItem(
						"cookie_agreed",
						"true",
						60 * 60 * 24 * 365,
						"/; SameSite=None; Secure"
					);
				}
			}
		}
	}, [showCookieNotice, entity]);

	return (
		<ActionsContext.Provider value={{ setEntity }}>
			<SingleContext.Provider
				value={{ ...entity, showCookieNotice, setShowCookieNotice }}
			>
				<PageResolver Login={LoginForm} Public={PublicLayout} Private={PrivateLayout}>
					<Helmet>
						<html className={htmlClass} />
						<title>
							{full_entity.title ? `${full_entity.title} - ${title}` : `${title}`}
						</title>
						<meta property="og:locale" content="en_US" />
						{
							<meta
								property="og:type"
								content={full_entity.isHome ? "website" : "article"}
							/>
						}
						{process.env.NODE_ENV === "production" &&
						!process.env.CLF_SITE_URL.includes("-stage") ? (
							<meta
								name="robots"
								content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
							/>
						) : (
							<meta name="robots" content="noindex, nofollow" />
						)}
						<meta property="og:site_name" content={title} />
						<meta property="og:title" content={`${full_entity.title} - ${title}`} />

						{!full_entity.isHome && full_entity.changed && (
							<meta property="article:modified_time" content={full_entity.changed} />
						)}

						{full_entity.slug && (
							<link
								rel="canonical"
								href={`${process.env.CLF_SITE_URL}${full_entity.slug}`}
							/>
						)}
						{full_entity.slug && (
							<meta
								property="og:url"
								content={`${process.env.CLF_SITE_URL}${full_entity.slug}`}
							/>
						)}
						{full_entity.summary && (
							<meta name="description" content={full_entity.summary} />
						)}
						{full_entity.summary && (
							<meta property="og:description" content={full_entity.summary} />
						)}
						{twitterUsername && (
							<meta name="twitter:site" content={`@${twitterUsername}`} />
						)}
						{full_entity.image && (
							<meta property="og:image" content={full_entity.image.src} />
						)}
						{full_entity.image && (
							<meta property="og:image:width" content={full_entity.image.width} />
						)}
						{full_entity.image && (
							<meta property="og:image:height" content={full_entity.image.height} />
						)}
						{full_entity.image && (
							<meta
								property="og:image:type"
								content={`image/${full_entity.image.format}`}
							/>
						)}
						{full_entity.image && (
							<meta name="twitter:image" content={full_entity.image.src} />
						)}
						{full_entity.image && (
							<meta name="twitter:image:alt" content={full_entity.title} />
						)}
						<meta name="twitter:description" content={full_entity.summary} />
						<meta name="twitter:title" content={`${full_entity.title} - ${title}`} />
						<meta name="twitter:card" content="summary_large_image" />

						{!full_entity.image && (
							<meta property="og:image" content={`${process.env.CLF_SITE_URL}/og.jpg`} />
						)}
						{!full_entity.image && <meta property="og:image:width" content={1280} />}
						{!full_entity.image && <meta property="og:image:height" content={720} />}
						{!full_entity.image && (
							<meta property="og:image:type" content={`image/jpg`} />
						)}
					</Helmet>
					{children}
				</PageResolver>
			</SingleContext.Provider>
		</ActionsContext.Provider>
	);
}

function PageResolver({ Login, Public, Private, Restricted, children }) {
	const { isPrivate, isAuthenticated, isIframe } = useContext(SiteContext);
	if (typeof Public === "undefined") {
		return <>{children}</>;
	}
	if (isIframe) {
		if ((isPrivate && typeof Private === "undefined") || !isPrivate) {
			return <Public>{children}</Public>;
		} else {
			return <Private>{children}</Private>;
		}
	} else {
		if (isPrivate && !isAuthenticated) {
			return <Public>{typeof Login !== "undefined" && <Login />}</Public>;
		} else if (isPrivate && isAuthenticated) {
			if (typeof Private === "undefined") {
				return <Public>{children}</Public>;
			} else {
				return <Private>{children}</Private>;
			}
		} else {
			return <Public>{children}</Public>;
		}
	}
}

export default Page;
